import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  FormHelperText,
  Container,
  Grid,
  Divider,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  SaveOutlined,
  TrendingUpOutlined,
  CheckCircleOutline,
} from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
const baseURL = process.env.REACT_APP_API_PATH;
const PriceManagement = () => {
  const [priceData, setPriceData] = useState({
    factorForLessThan2: "",
    factorFor2To5: "",
    factorForMoreThan5: "",
  });
  const [priceId, setPriceId] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchPriceData = async () => {
      await axios
        .get(`${baseURL}/ebench/candidate/pricing`)
        .then((response) => {
          const data = response?.data.data;
          setPriceId(data.id);
          setPriceData({
            factorForLessThan2: data.factorForLessThan2 || "",
            factorFor2To5: data.factorFor2To5 || "",
            factorForMoreThan5: data.factorForMoreThan5 || "",
          });
        });
    };
    fetchPriceData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setPriceData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (value === "" || isNaN(value) || Number(value) <= 0) {
      setErrors((prev) => ({
        ...prev,
        [name]: "Enter a positive number",
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const hasErrors = Object.values(priceData).some(
      (val) => val === "" || isNaN(val) || Number(val) <= 0
    );

    if (hasErrors) {
      return;
    }
    const payload = {
      id: priceId,
      factorForLessThan2: parseFloat(priceData.factorForLessThan2),
      factorFor2To5: parseFloat(priceData.factorFor2To5),
      factorForMoreThan5: parseFloat(priceData.factorForMoreThan5),
    };
    await axios
      .put(`${baseURL}/ebench/candidate/pricing/update`, payload)
      .then((response) => {
        if (response.data.status) {
          toast.success("Pricing factors updated successfully");
        } else {
          toast.error("Failed to update pricing factors");
        }
      });
  };

  const fields = [
    {
      label: "Factor for experience <=2",
      name: "factorForLessThan2",
      description: "Applied to candidates with less than 2 years of experience",
    },
    {
      label: "Factor for experience >=2 to <=5",
      name: "factorFor2To5",
      description: "Applied to candidates with 2 to 5 years of experience",
    },
    {
      label: "Factor for experience >=5",
      name: "factorForMoreThan5",
      description: "Applied to candidates with more than 5 years of experience",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f7fa",
        py: 4,
        px: 2,
      }}
    >
      <Container maxWidth="xl">
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 4,
              py: 3,
              bgcolor: "white",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <TrendingUpOutlined
                sx={{ mr: 1.5, color: "primary.main", fontSize: 28 }}
              />
              <Typography variant="h5" fontWeight={600} color="text.primary">
                Price Management
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Configure pricing factors based on candidate experience levels.
            </Typography>
          </Box>

          {/* Form Content */}
          <Box sx={{ px: 4, py: 4, bgcolor: "white" }}>
            <Grid container spacing={3}>
              {fields.map((field, index) => (
                <Grid item xs={12} md={4} key={field.name}>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      gutterBottom
                      color="text.primary"
                    >
                      {field.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1.5 }}
                    >
                      {field.description}
                    </Typography>
                    <TextField
                      name={field.name}
                      value={priceData[field.name]}
                      onChange={handleChange}
                      type="number"
                      placeholder="0.00"
                      fullWidth
                      error={!!errors[field.name]}
                      size="medium"
                      InputProps={{
                        inputProps: { step: "0.01", min: "0" },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "background.paper",
                          "&:hover": {
                            bgcolor: "#fafbfc",
                          },
                        },
                      }}
                    />
                    {errors[field.name] && (
                      <FormHelperText error sx={{ mt: 0.5 }}>
                        {errors[field.name]}
                      </FormHelperText>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={
                  Object.values(errors).some((e) => e) ||
                  Object.values(priceData).some((v) => !v)
                }
                startIcon={<SaveOutlined />}
                sx={{
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: 1,
                  "&:hover": {
                    boxShadow: 2,
                  },
                }}
              >
                Save Changes
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setPriceData({
                    factorForLessThan2: "",
                    factorFor2To5: "",
                    factorForMoreThan5: "",
                  });
                  setErrors({});
                }}
                sx={{
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Reset
              </Button>
            </Box>
          </Box>

          {/* Info Footer */}
          <Box
            sx={{
              px: 4,
              py: 2.5,
              bgcolor: "#f8f9fa",
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              <strong>Note:</strong> Changes will apply to all new orders
              immediately after saving. Pricing factors are multipliers applied
              to base prices.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PriceManagement;
